import { useEffect, useState } from "react";

import { gql } from "@apollo/client";
import { LoadingSlide } from "@components/title-slide-view";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import {
  type AssignCollectorIpAddressIfNotYetAssigned,
  type FetchCollectorSubdomain as FetchCollectorSubdomainQuery,
} from "@north-beam/nb-client-side";
import { useOnboardingStep } from "@pages/onboarding/onboarding-nav/use-onboarding-number";
import { Steps } from "@pages/onboarding/steps";

import cloudflare from "./images/cloudflare.png";
import dnsIconCloudflare from "./images/dnsIconCloudflare.png";
import genericARecordImage from "./images/genericARecordImage.png";
import goDaddy from "./images/goDaddyARecord.png";
import googleDomains from "./images/googleDomains.png";

export default function DnsEntry({
  selectedProvider,
  goToNamedStep,
}: {
  selectedProvider?: string;
  goToNamedStep?: (step: string) => void;
}) {
  const [ipAddress, setIpAddress] = useState<string>("");

  const { designElementManager } = useDesignElementManager();

  const { loading: fetchSubdomainQueryLoading, data: subdomainData } =
    useNorthbeamQuery<FetchCollectorSubdomainQuery>(FETCH_COLLECTOR_SUBDOMAIN);

  const [assignCollectorIpAddressIfNotYetAssigned, { data, loading }] =
    useNorthbeamMutation<AssignCollectorIpAddressIfNotYetAssigned>(
      ASSIGN_COLLECTOR_IP_ADDRESS_IF_NOT_YET_ASSIGNED,
    );

  useEffect(() => {
    assignCollectorIpAddressIfNotYetAssigned();
  }, [assignCollectorIpAddressIfNotYetAssigned]);

  useEffect(() => {
    setIpAddress(data?.assignCollectorIpAddressIfNotYetAssigned || "");
  }, [data]);

  let display = <></>;

  switch (selectedProvider) {
    case "other":
      display = (
        <>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              Generic DNS details
            </div>
          </h3>
          <div className="font-semibold text-interstellar+0 pb-2">
            Resources
          </div>
          <ol className="list-disc pl-6">
            <li className="text-cosmic+0">
              <a href="https://docs.northbeam.io/docs/link-dns">
                Northbeam's Documentation
              </a>
            </li>
          </ol>
          <div className="font-semibold text-interstellar+0 pb-2">
            About DNS connections
          </div>
          <p>
            Setting up a DNS entry improves our ability to track your customers
            when they return to your website after their first visit. The
            instructions below will walk you through adding an A record in your
            DNS that points to {ipAddress}.
          </p>
          <div className="font-semibold text-interstellar+0 pb-2">
            Top level domain examples
          </div>
          <div className="flex flex-row gap-1 items-center">
            <code>i.northbeam.io</code>
            <i className="fa-solid fa-arrow-right"></i>
            <div className="text-center">the top level domain here is</div>
            <code>northbeam.io</code>
          </div>
          <div className="flex flex-row gap-1 items-center pb-10">
            <code>i.company-2.com</code>
            <i className="fa-solid fa-arrow-right"></i>
            <div className="text-center">the top level domain here is</div>
            <code>company-2.com</code>
          </div>
          <div className="h-[1px] bg-gray-0 w-full my-6"></div>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              1. Log into your DNS provider
            </div>
          </h3>
          <div className="h-[1px] bg-gray-0 w-full my-6"></div>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              2. Configure DNS
            </div>
          </h3>
          <div className="font-semibold text-interstellar+0 pb-2">
            Make sure you’ve followed these steps:
          </div>
          <div className="flex flex-row gap-2">
            <div>1.</div>
            <div>
              In your DNS, add an <code>A</code> record that points the subdoman{" "}
              <code>i</code> to <code>{ipAddress}</code>
            </div>
          </div>
          <div className="ml-5 flex flex-row gap-1">
            <div className="font-semibold">Note:</div>
            <div>
              In the below example ’Host’ would be <code>i</code> and ’Points
              to’ would be <code>{ipAddress}</code>
            </div>
          </div>
          <img
            className="img-fluid mt-2 mb-4"
            src={genericARecordImage}
            alt="Generic DNS A record"
          />
          <div className="ml-5 flex flex-row">
            <div className="font-semibold">Note:</div>
            <div>
              If the subdomain <code>i</code> is already mapped to something,
              Contact us for help choosing a new subdomain name
            </div>
          </div>
        </>
      );
      break;
    case "cloudflare":
      display = (
        <>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              Cloudflare DNS details
            </div>
          </h3>
          <div className="font-semibold text-interstellar+0 pb-2">
            Resources
          </div>
          <ol className="list-disc pl-6">
            <li className="text-cosmic+0">
              <a href="https://docs.northbeam.io/docs/link-dns">
                Northbeam's Documentation
              </a>
            </li>
          </ol>
          <div className="font-semibold text-interstellar+0 pb-2">
            About DNS connections
          </div>
          <p>
            Setting up a DNS entry improves our ability to track your customers
            when they return to your website after their first visit. The
            instructions below will walk you through adding an A record in your
            DNS that points to {ipAddress}.
          </p>
          <div className="font-semibold text-interstellar+0 pb-2">
            Top level domain examples
          </div>
          <div className="flex flex-row gap-1 items-center">
            <code>i.northbeam.io</code>
            <i className="fa-solid fa-arrow-right"></i>
            <div className="text-center">the top level domain here is</div>
            <code>northbeam.io</code>
          </div>
          <div className="flex flex-row gap-1 items-center pb-10">
            <code>i.company-2.com</code>
            <i className="fa-solid fa-arrow-right"></i>
            <div className="text-center">the top level domain here is</div>
            <code>company-2.com</code>
          </div>
          <div className="font-semibold text-interstellar+0 pb-2">
            A note about Cloudflare
          </div>
          <p>
            Setting up on Cloudflare resembles the other DNS providers, with one
            exception: you will want to turn off proxying.
          </p>
          <div className="h-[1px] bg-gray-0 w-full my-6"></div>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              1. Log into Cloudflare
            </div>
          </h3>
          <p>
            <a
              className="font-semibold uppercase py-2 px-4 text-white bg-cosmic rounded-2xl no-underline mb-10"
              target="_blank"
              href="https://dash.cloudflare.com/login"
              rel="noreferrer"
            >
              Open Cloudflare
            </a>
          </p>
          <div className="h-[1px] bg-gray-0 w-full my-6"></div>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              2. Configure DNS
            </div>
          </h3>
          <div className="font-semibold text-interstellar+0 pb-2">
            Make sure you’ve followed these steps:
          </div>
          <div className="flex flex-row gap-2">
            <div>2a.</div>
            <div>Click on the ‘DNS’ section</div>
          </div>
          <img
            className="img-fluid mt-2 mb-4"
            src={dnsIconCloudflare}
            alt="Cloudflare DNS icon screencap"
          />
          <div className="flex flex-row gap-2">
            <div>2b.</div>
            <div>Ad a record as shown, taking care to turn off proxying</div>
          </div>
          <div className="flex flex-col gap-2 mb-2 ml-7">
            <div className="flex flex-row gap-2">
              <div>2ba.</div>
              <div>
                Set the ‘Type’ to <code>A</code>.
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div>2bb.</div>
              <div>
                Set the ‘Name’ to <code>i</code>.
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div>2bc.</div>
              <div>
                Set the ‘Content’ to <code>{ipAddress}</code>.
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div>2bd.</div>
              <div>
                Set the ‘TTL’ to <code>Auto</code>.
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div>2be.</div>
              <div>
                Set the ‘Proxy status’ to <code>DNS only</code>.
              </div>
            </div>
            <img
              className="img-fluid"
              src={cloudflare}
              alt="Cloudflare screencap"
            />
          </div>
        </>
      );
      break;
    case "go_daddy":
      display = (
        <>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              Cloudflare DNS details
            </div>
          </h3>
          <div className="font-semibold text-interstellar+0 pb-2">
            Resources
          </div>
          <ol className="list-disc pl-6">
            <li className="text-cosmic+0">
              <a href="https://docs.northbeam.io/docs/link-dns">
                Northbeam's Documentation
              </a>
            </li>
            <li className="text-cosmic+0">
              <a href="https://www.godaddy.com/help/add-an-a-record-19238">
                GoDaddy’s official documentation
              </a>
            </li>
          </ol>
          <div className="font-semibold text-interstellar+0 pb-2">
            About DNS connections
          </div>
          <p>
            Setting up a DNS entry improves our ability to track your customers
            when they return to your website after their first visit. The
            instructions below will walk you through adding an A record in your
            DNS that points to {ipAddress}.
          </p>
          <div className="font-semibold text-interstellar+0 pb-2">
            Top level domain examples
          </div>
          <div className="flex flex-row gap-1 items-center">
            <code>i.northbeam.io</code>
            <i className="fa-solid fa-arrow-right"></i>
            <div className="text-center">the top level domain here is</div>
            <code>northbeam.io</code>
          </div>
          <div className="flex flex-row gap-1 items-center pb-10">
            <code>i.company-2.com</code>
            <i className="fa-solid fa-arrow-right"></i>
            <div className="text-center">the top level domain here is</div>
            <code>company-2.com</code>
          </div>
          <div className="h-[1px] bg-gray-0 w-full my-6"></div>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              1. Log into GoDaddy
            </div>
          </h3>
          <p>
            <a
              className="font-semibold uppercase py-2 px-4 text-white bg-cosmic rounded-2xl inline no-underline mb-10"
              target="_blank"
              href="https://sso.godaddy.com/?realm=idp&app=cart&path=%2Fcheckoutapi%2Fv1%2Fredirects%2Flogin"
              rel="noreferrer"
            >
              Open GoDaddy
            </a>
          </p>
          <div className="h-[1px] bg-gray-0 w-full my-6"></div>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              2. Configure DNS
            </div>
          </h3>
          <div className="font-semibold text-interstellar+0 pb-2">
            Make sure you’ve followed these steps:
          </div>
          <div className="flex flex-row gap-2">
            <div>1.</div>
            <div>
              Enter <code>i</code> in the 'Host' field.
            </div>
          </div>
          <div className="ml-5 flex flex-row">
            <div className="font-semibold">Note:</div>
            <div>
              If the ‘Host’ field is already set to<code>i</code>, Contact us
              for help choosing a new subdomain name
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div>2.</div>
            <div>
              Enter <code>{ipAddress}</code> In the ‘Points to’ field.
            </div>
          </div>
          <img
            className="img-fluid mt-2 mb-4"
            src={goDaddy}
            alt="GoDaddy DNS setup screencap"
          />
        </>
      );
      break;
    case "google":
      display = (
        <>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              Google Domains DNS details
            </div>
          </h3>
          <div className="font-semibold text-interstellar+0 pb-2">
            Resources
          </div>
          <ol className="list-disc pl-6">
            <li className="text-cosmic+0">
              <a href="https://docs.northbeam.io/docs/link-dns">
                Northbeam's Documentation
              </a>
            </li>
            <li className="text-cosmic+0">
              <a href="https://support.google.com/domains/answer/9211383?hl=en">
                Google's Official Documentation
              </a>
            </li>
          </ol>
          <div className="font-semibold text-interstellar+0 pb-2">
            About DNS connections
          </div>
          <p>
            Setting up a DNS entry improves our ability to track your customers
            when they return to your website after their first visit. The
            instructions below will walk you through adding an A record in your
            DNS that points to {ipAddress}.
          </p>
          <div className="font-semibold text-interstellar+0 pb-2">
            Top level domain examples
          </div>
          <div className="flex flex-row gap-1 items-center">
            <code>i.northbeam.io</code>
            <i className="fa-solid fa-arrow-right"></i>
            <div className="text-center">the top level domain here is</div>
            <code>northbeam.io</code>
          </div>
          <div className="flex flex-row gap-1 items-center pb-10">
            <code>i.company-2.com</code>
            <i className="fa-solid fa-arrow-right"></i>
            <div className="text-center">the top level domain here is</div>
            <code>company-2.com</code>
          </div>
          <div className="h-[1px] bg-gray-0 w-full my-6"></div>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              1. Log into Google Domains
            </div>
          </h3>
          <p>
            <a
              className="font-semibold uppercase py-2 px-4 text-white bg-cosmic rounded-2xl w-[194px] no-underline mb-10"
              target="_blank"
              href="http://domains.google.com/registrar"
              rel="noreferrer"
            >
              Open Google domains
            </a>
          </p>
          <div className="h-[1px] bg-gray-0 w-full my-6"></div>
          <h3 className="mt-4 mb-3">
            <div className="text-lg font-semibold text-interstellar+0">
              2. Configure DNS
            </div>
          </h3>
          <div className="font-semibold text-interstellar+0 pb-2">
            Make sure you’ve followed these steps:
          </div>
          <div className="flex flex-row gap-2">
            <div>2a.</div>
            <div>Click on the domain you want to update</div>
          </div>
          <div className="flex flex-row gap-2">
            <div>2b.</div>
            <div>Open the hamburger menu</div>
          </div>
          <div className="ml-8 my-2">
            <svg
              width="34"
              height="34"
              viewBox="0 0 34 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.5"
                y="0.5"
                width="33"
                height="33"
                rx="3.5"
                stroke="#C1C5CD"
              />
              <rect
                x="8"
                y="8"
                width="18"
                height="18"
                fill="url(#pattern0_2430_284801)"
              />
              <defs>
                <pattern
                  id="pattern0_2430_284801"
                  patternContentUnits="objectBoundingBox"
                  width="1"
                  height="1"
                >
                  <use
                    xlinkHref="#image0_2430_284801"
                    transform="scale(0.0555556)"
                  />
                </pattern>
                <image
                  id="image0_2430_284801"
                  width="18"
                  height="18"
                  // eslint-disable-next-line max-len
                  xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAASCAQAAAD8x0bcAAAAAnNCSVQICFXsRgQAAABeSURBVCiRY2AYfICRgYGBoWgFo+j//1gkGf+/7otgYGBhYGBgYPZg58eihoGR4edHWrjpArPU33+YksxMf5/1GTAwMFHXTqLcVPoBdxB0C0DD6e+O7zgDk6ZOpAAAAE/sGwTi9e7AAAAAAElFTkSuQmCC"
                />
              </defs>
            </svg>
          </div>
          <div className="flex flex-row gap-2">
            <div>2c.</div>
            <div>Open the hamburger menu</div>
          </div>
          <div className="ml-8 my-2">
            <svg
              width="34"
              height="34"
              viewBox="0 0 34 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.5"
                y="0.5"
                width="33"
                height="33"
                rx="3.5"
                stroke="#C1C5CD"
              />
              <rect
                x="8"
                y="8"
                width="18"
                height="18"
                fill="url(#pattern0_2433_285129)"
              />
              <defs>
                <pattern
                  id="pattern0_2433_285129"
                  patternContentUnits="objectBoundingBox"
                  width="1"
                  height="1"
                >
                  <use
                    xlinkHref="#image0_2433_285129"
                    transform="scale(0.0555556)"
                  />
                </pattern>
                <image
                  id="image0_2433_285129"
                  width="18"
                  height="18"
                  // eslint-disable-next-line max-len
                  xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAASCAQAAAD8x0bcAAAAAnNCSVQICFXsRgQAAADsSURBVCiRrZA/SgNREIe/eW/XhFjYiLCpzCU8QNJZS8TWA1goe4qAuYWQ7S3NBXKQoIWwiDGwzjwL48NNXhpxppyP35+B/xoBuH1wI8wEax+d1/n0CjIAN8pOdEWdF2FbYQjgADBdTQ7v+5+VEFprGiETrQGahSQzfdsFX9zNmsVBafshXed0xt2xsZ3J1hF6vjg+D5qo7l8fYyZxSZd4yQD6VXYqCLt2/prBRil0jY/qrWxeDG2tdKKSiS2nl3BDb5KI9vMCOQLIzkIC2UA43yvftc6LwO93Cs5HSOcMMVyzbCs4b0/7ev9tvgAcNlZEO0S4xAAAAABJRU5ErkJggg=="
                />
              </defs>
            </svg>
          </div>
          <div className="flex flex-row gap-2 mb-2">
            <div>2d.</div>
            <div>
              Add a ‘New record’ in the ‘Custom resource records’ section
            </div>
          </div>
          <div className="flex flex-row gap-2 mb-2">
            <div>2e.</div>
            <div>
              Add a ‘New record’ in the ‘Custom resource records’ section
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2 ml-7">
            <div className="flex flex-row gap-2">
              <div>2ea.</div>
              <div>
                Enter <code>i</code> in the first field.
              </div>
            </div>
            <div className="ml-5 flex flex-row">
              <div className="font-semibold">Note:</div>
              <div>
                If there is already an entry with ‘Name’ set to <code>i</code>{" "}
                in the ‘Custom resource records’ list, Contact us for help
                choosing a new subdomain name
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2 ml-7">
            <div className="flex flex-row gap-2">
              <div>2eb.</div>
              <div>
                In the dropdown, select <code>A</code>.
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2 ml-7">
            <div className="flex flex-row gap-2">
              <div>2ec.</div>
              <div>
                Set the ‘TTL’ to <code>5m</code>.
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2 ml-7">
            <div className="flex flex-row gap-2">
              <div>2ed.</div>
              <div>
                Enter <code>{ipAddress}</code> in the last filed
              </div>
            </div>
          </div>
          <img
            className="img-fluid"
            src={googleDomains}
            alt="Google Domains screencap"
          />
        </>
      );
      break;
  }

  const providers = [
    {
      name: "google",
      label: (
        <>
          Google
          <br />
          Domains
        </>
      ),
      helpTitle: "Google Domains",
      title: "Connect a Google Domains DNS",
      designElementName: "google-domains",
    },
    {
      name: "cloudflare",
      label: "Cloudflare",
      helpTitle: "Cloudflare",
      designElementName: "cloudflare",
      title: "Connect a Cloudflare DNS",
    },
    {
      name: "go_daddy",
      label: "GoDaddy",
      helpTitle: "GoDaddy",
      designElementName: "go-daddy",
      title: "Connect a GoDaddy DNS",
    },
    {
      name: "other",
      title: "Connect your DNS",
      helpTitle: "your DNS provider",
      label: (
        <>
          Other
          <br />
          (Generic DNS)
        </>
      ),
      designIcon: (
        <div className=" h-8 w-8 rounded-full bg-cosmic-4 border flex justify-center items-center border-gray-1">
          <i className="fa-regular fa-server text-cosmic" />
        </div>
      ),
    },
  ];

  const { stepDictionary } = useOnboardingStep();
  const stepIndex = stepDictionary["dns-setup"].index;

  const selectedProviderObj = providers.find(
    (providerItem) => providerItem.name === selectedProvider,
  );
  return (
    <>
      {(loading || fetchSubdomainQueryLoading) && <LoadingSlide />}
      {!loading &&
        !fetchSubdomainQueryLoading &&
        ipAddress &&
        selectedProvider && (
          <div className="border-t border-gray-0 pb-10">
            <div className="px-10 pt-10 flex flex-col">
              <div className="justify-start items-center gap-2 inline-flex pb-7">
                <div className="w-10 h-10">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full">
                    {selectedProviderObj?.designElementName
                      ? designElementManager
                          .getDesignElement(
                            selectedProviderObj?.designElementName,
                          )
                          .getComponent({ size: "32" })
                      : selectedProviderObj?.designIcon}
                  </div>
                </div>
                <div className="text-slate-900 text-2xl font-semibold leading-loose">
                  {stepIndex}b. {selectedProviderObj?.title}
                </div>
                <div className="uppercase bg-interstellar-3 rounded-2xl text-interstellar+0 font-semibold p-1 flex items-center justify-center gap-2">
                  <div className="bg-gray-0 h-6 w-6 rounded-full justify-center flex items-center">
                    <i className="fa-solid fa-times text-white"></i>
                  </div>
                  Not Connected
                </div>
              </div>
              <div className="flex flex-row gap-2 pb-6">
                <div className="flex items-center gap-2">
                  <div className="w-[18px] h-[18px] bg-cosmic rounded-full border border-black flex justify-center items-center">
                    <i
                      className="fa-solid fa-user-plus fa-2xs"
                      style={{ color: "#ffffff" }}
                    ></i>
                  </div>
                  <div>
                    Don't have log-in credentials for{" "}
                    {selectedProviderObj?.helpTitle}
                  </div>
                </div>
                <button
                  onClick={() => goToNamedStep?.(Steps.ACCOUNT_MANAGERS)}
                  className="justify-start items-end gap-4 inline-flex underline text-cosmic+0"
                >
                  Invite an account manager
                </button>
              </div>
              {display}
            </div>
          </div>
        )}
      {!loading && !fetchSubdomainQueryLoading && !ipAddress && (
        <p>
          Unfortunately, we are currently at maximum IP Address capacity. Please
          check back in a couple of hours.
        </p>
      )}
    </>
  );
}

export const ASSIGN_COLLECTOR_IP_ADDRESS_IF_NOT_YET_ASSIGNED = gql`
  mutation AssignCollectorIpAddressIfNotYetAssignedV2 {
    assignCollectorIpAddressIfNotYetAssigned
  }
`;

export const FETCH_COLLECTOR_SUBDOMAIN = gql`
  query FetchCollectorSubdomainV2 {
    me {
      collectorSubdomain
    }
  }
`;
